import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "./reduxHooks";
import { selectOpenSequence } from "../DocumentPreviewSlice_Store";
import type { DocumentTab, OpenDocument } from "../components/deals/WorkspaceTabBar";
import type { DealSelection, WorkspaceResults } from "./useWorkspaceSelection";

/**
 * Human-readable tab label: research docs expose researchTitle (their
 * originalFilename is an opaque source ID), deal docs use originalFilename.
 * Must be used everywhere a tab is named or looked up so the activeTab string
 * stays consistent across open/close/switch logic.
 */
export function getDocTabName(doc: { researchTitle?: string; originalFilename?: string }): string {
  return doc.researchTitle || doc.originalFilename || "Document Preview";
}

/**
 * Base tabs are hidden entirely for offer-letter, and the "Deals" tab is hidden
 * when the inline deals flag is on. For research-desk the workspace "Docs" tab
 * disappears once a preview is open (keeps the UX clean after a row click) but
 * stays present with no preview so the workspace doesn't render blank when
 * setLayoutMode('workspace') fires on facts-arrival with no click.
 */
function computeBaseTabs(
  effectiveUseCase: string | null,
  inlineDealsFlag: boolean,
  openDocumentCount: number
): string[] {
  if (effectiveUseCase === "offer-letter") return [];
  if (effectiveUseCase === "research-desk") {
    return openDocumentCount > 0 ? [] : ["Docs"];
  }
  return inlineDealsFlag ? ["Docs"] : ["Deals", "Docs"];
}

interface TabSwitchContext {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

/**
 * Keep activeTab pointing at a tab that still exists. Deal tabs replace base
 * tabs when a deal is selected, so they are first-class members of the list.
 */
function useFallbackTab(
  { activeTab, setActiveTab }: TabSwitchContext,
  showWorkspace: boolean,
  allTabNames: string[]
): void {
  useEffect(() => {
    if (showWorkspace && allTabNames.length > 0 && !allTabNames.includes(activeTab)) {
      setActiveTab(allTabNames[0]);
    }
    // setActiveTab is a stable state setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWorkspace, allTabNames, activeTab]);
}

/**
 * Auto-switch to the deal panel tabs when a deal is selected. When the deal
 * closes, allTabNames changes and useFallbackTab picks the first valid tab.
 */
function useDealTabSwitch(
  { setActiveTab }: TabSwitchContext,
  deal: { hasDealSelected: boolean; selectedDealId: string | null; selectedDealTab: string },
  onShowResults: () => void
): void {
  const prevSelectedDealId = useRef(deal.selectedDealId);
  const onShowResultsRef = useRef(onShowResults);
  onShowResultsRef.current = onShowResults;

  useEffect(() => {
    if (deal.hasDealSelected && deal.selectedDealId !== prevSelectedDealId.current) {
      setActiveTab(deal.selectedDealTab === "docs" ? "DealDocs" : "DealSummary");
      onShowResultsRef.current(); // On mobile, flip to RHS view
    }
    prevSelectedDealId.current = deal.selectedDealId;
    // setActiveTab is a stable state setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.selectedDealId, deal.hasDealSelected, deal.selectedDealTab]);
}

/** Auto-switch to the preview tab on every openDocumentPreview dispatch. */
function useDocumentOpenSwitch(
  { setActiveTab }: TabSwitchContext,
  documents: { openDocuments: OpenDocument[]; activeDocumentId: string | null },
  onShowResults: () => void
): void {
  const openSequence = useAppSelector(selectOpenSequence);
  const prevOpenSequence = useRef(openSequence);
  const onShowResultsRef = useRef(onShowResults);
  onShowResultsRef.current = onShowResults;

  useEffect(() => {
    if (openSequence !== prevOpenSequence.current) {
      // A document was explicitly opened (fact badge, source link, etc.)
      const activeDoc = documents.openDocuments.find(
        (doc) => doc?.docId === documents.activeDocumentId
      );
      if (documents.activeDocumentId && activeDoc) {
        setActiveTab(getDocTabName(activeDoc));
      }
      onShowResultsRef.current(); // On mobile, flip to the Deals & Docs view
    }
    prevOpenSequence.current = openSequence;
    // setActiveTab is a stable state setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSequence, documents.activeDocumentId, documents.openDocuments]);
}

export interface WorkspaceTabs {
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  baseTabs: string[];
  documentTabs: DocumentTab[];
}

/** Owns which workspace tab is live and the tab lists that feed the tab bar. */
export function useWorkspaceTabs(
  deal: DealSelection,
  results: WorkspaceResults,
  onShowResults: () => void
): WorkspaceTabs {
  // 'Deals' is the default tab
  const [activeTab, setActiveTab] = useState<string>("Deals");
  const context: TabSwitchContext = { activeTab, setActiveTab };

  const baseTabs = useMemo(
    () =>
      computeBaseTabs(results.effectiveUseCase, deal.inlineDealsFlag, results.openDocuments.length),
    [results.effectiveUseCase, deal.inlineDealsFlag, results.openDocuments.length]
  );

  const documentTabs: DocumentTab[] = results.openDocuments.map((doc) => ({
    id: doc?.docId ?? "",
    name: getDocTabName(doc ?? {}),
    document: doc,
  }));

  // Deal tabs replace the base tabs while a deal is open
  const allTabNames = [
    ...(deal.hasDealSelected ? ["DealSummary", "DealDocs"] : baseTabs),
    ...documentTabs.map((tab) => tab.name),
  ];

  useFallbackTab(context, results.showWorkspace, allTabNames);
  useDealTabSwitch(context, deal, onShowResults);
  useDocumentOpenSwitch(context, results, onShowResults);

  return { activeTab, setActiveTab, baseTabs, documentTabs };
}
